import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Icon, ProviderMark } from "./icons";
import { Button, Field, Input, Select, Spinner, cx } from "./ui";
import { relativeTime } from "../lib/format";
import { useGitBranches, useGitConnections, useGitRepos } from "../lib/queries";
import type { GitRepo } from "../lib/types";
import { ConnectSigninHost, useSigninHostCandidate } from "./ConnectSigninHost";
import { useAuth } from "../auth/AuthProvider";

export interface RepoSelection {
  connectionId: string;
  owner: string;
  repository: string;
  branch: string;
}

/**
 * Connection → repository → branch, in that order.
 *
 * Repositories arrive a page at a time and are filtered in the browser rather
 * than on the server: git hosts have no "search my repositories by substring"
 * endpoint worth the round trip, and a customer with 40 repositories — which is
 * most of them — gets an instant filter this way. "Load more" exists for the
 * ones who have hundreds, and says plainly that it is only filtering what has
 * been loaded.
 */
export function RepoPicker({
  orgId,
  value,
  onChange,
}: {
  orgId: string | undefined;
  value: RepoSelection | null;
  onChange: (selection: RepoSelection | null) => void;
}) {
  const { t } = useTranslation("create");
  const connections = useGitConnections(orgId);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [loadedPages, setLoadedPages] = useState<GitRepo[]>([]);
  const [filter, setFilter] = useState("");

  // Default to the only connection, or the first one, without making the
  // customer choose when there is nothing to choose between.
  const active = connectionId ?? connections.data?.[0]?.id ?? null;

  const repos = useGitRepos(orgId, active, page);

  // Accumulate pages. Keyed by id so a refetch of page 1 cannot duplicate rows.
  const allRepos = useMemo(() => {
    const merged = new Map<string, GitRepo>();
    for (const repo of [...loadedPages, ...(repos.data?.data ?? [])]) merged.set(repo.id, repo);
    return [...merged.values()];
  }, [loadedPages, repos.data]);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return allRepos;
    return allRepos.filter((repo) => repo.fullName.toLowerCase().includes(needle));
  }, [allRepos, filter]);

  const selected = value ? allRepos.find((r) => r.fullName === `${value.owner}/${value.repository}`) : undefined;

  const branches = useGitBranches(
    orgId,
    value?.connectionId ?? null,
    value ? { owner: value.owner, repository: value.repository } : null,
  );

  function pick(repo: GitRepo) {
    if (!active) return;
    onChange({
      connectionId: active,
      owner: repo.owner,
      repository: repo.name,
      branch: repo.defaultBranch,
    });
  }

  const signinHost = useSigninHostCandidate(orgId);
  // Connecting a host is an admin's act; a developer sees the link to the
  // page that says so rather than a button that would be refused.
  const { currentOrg } = useAuth();
  const canManage = currentOrg?.role === "owner" || currentOrg?.role === "admin";

  if (connections.isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-fg-subtle">
        <Spinner className="size-4" />
        {t("repo.loadingConnections")}
      </div>
    );
  }

  if ((connections.data ?? []).length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-line-strong p-6 text-center">
        <span className="mx-auto grid size-11 place-items-center rounded-full bg-raised text-fg-subtle">
          <Icon name="branch" className="size-5" />
        </span>
        <p className="mt-3 font-medium text-fg">{t("repo.noHost")}</p>
        {/* The two hosts the API actually offers. Bitbucket was named here and
            is not one of them: it exists in the database enum so that adding it
            needs no migration, and `providers/git/index.ts` has never mapped an
            adapter for it. */}
        <p className="mx-auto mt-1 max-w-sm text-sm text-fg-subtle">{t("repo.connectLead")}</p>
        {/* Signed in with a host the organization has not connected: the
            connection starts here, and comes back to this form. */}
        {signinHost && canManage ? (
          <div className="mx-auto mt-4 max-w-md text-left">
            <ConnectSigninHost orgId={orgId} canManage redirectTo="/apps/new" compact />
          </div>
        ) : (
          <Link
            to="/settings/integrations"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-accent-hover"
          >
            {t("repo.connect")}
            <Icon name="arrow-up-right" className="size-4" />
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {(connections.data ?? []).length > 1 && (
        <Field label={t("repo.account")} htmlFor="git-connection">
          <Select
            id="git-connection"
            value={active ?? ""}
            onChange={(e) => {
              setConnectionId(e.target.value);
              setLoadedPages([]);
              setPage(1);
              onChange(null);
            }}
          >
            {(connections.data ?? []).map((connection) => (
              <option key={connection.id} value={connection.id}>
                {connection.account.login} ({connection.provider})
              </option>
            ))}
          </Select>
        </Field>
      )}

      {selected || value ? (
        <div className="flex items-center gap-3 rounded-lg border border-accent/40 bg-accent-soft p-3">
          {/*
            Falls back to the connection being browsed rather than to GitHub. A
            literal default draws a GitHub mark beside a GitLab repository, which
            is a wrong answer where the mark exists to give the right one.
          */}
          <ProviderMark
            provider={
              connections.data?.find((c) => c.id === (value?.connectionId ?? active))?.provider ??
              "github"
            }
            className="size-5 shrink-0 text-fg"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-fg">
              {value?.owner}/{value?.repository}
            </p>
            {selected?.private && (
              <p className="text-xs text-fg-subtle">{t("repo.private")}</p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => onChange(null)} type="button">
            {t("repo.change")}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <Icon
              name="search"
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle"
            />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t("repo.filter")}
              className="pl-9"
              aria-label={t("repo.filterLabel")}
            />
          </div>

          <div className="max-h-72 overflow-y-auto rounded-lg border border-line">
            {repos.isLoading && allRepos.length === 0 ? (
              <div className="flex items-center gap-2 p-4 text-sm text-fg-subtle">
                <Spinner className="size-4" />
                {t("repo.loadingRepos")}
              </div>
            ) : repos.isError ? (
              <p className="p-4 text-sm text-danger">
                {repos.error instanceof Error
                  ? repos.error.message
                  : t("repo.reposError")}
              </p>
            ) : filtered.length === 0 ? (
              <p className="p-4 text-sm text-fg-subtle">
                {filter ? t("repo.noMatch", { filter }) : t("repo.noRepos")}
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {filtered.map((repo) => (
                  <li key={repo.id}>
                    <button
                      type="button"
                      onClick={() => pick(repo)}
                      className={cx(
                        "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors",
                        "hover:bg-raised",
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-sm text-fg">
                            {repo.fullName}
                          </span>
                          {repo.private && (
                            <Icon name="lock" className="size-3.5 shrink-0 text-fg-subtle" />
                          )}
                        </span>
                        {repo.updatedAt && (
                          <span className="block text-xs text-fg-subtle">
                            Updated {relativeTime(repo.updatedAt)}
                          </span>
                        )}
                      </span>
                      <Icon name="chevron" className="size-4 shrink-0 text-fg-subtle" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {repos.data?.hasMore && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-fg-subtle">
                Showing {allRepos.length} repositories. The filter searches only these.
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={repos.isFetching}
                onClick={() => {
                  setLoadedPages(allRepos);
                  setPage((p) => p + 1);
                }}
              >
                {t("repo.loadMore")}
              </Button>
            </div>
          )}
        </div>
      )}

      {value && (
        <Field
          label={t("repo.branch")}
          htmlFor="git-branch"
          hint={branches.isLoading ? t("repo.loadingBranches") : undefined}
        >
          <Select
            id="git-branch"
            value={value.branch}
            disabled={branches.isLoading}
            onChange={(e) => onChange({ ...value, branch: e.target.value })}
          >
            {/* Keep the current value selectable even before the list arrives,
                so the field never appears empty mid-load. */}
            {(branches.data ?? [{ name: value.branch, isDefault: true }]).map((branch) => (
              <option key={branch.name} value={branch.name}>
                {branch.isDefault
                  ? t("repo.defaultBranch", { branch: branch.name })
                  : branch.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
    </div>
  );
}
